import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Modal from "../components/ui/Modal";
import {
  useEventTypesQuery,
  useAddEventTypeMutation,
  useUpdateEventTypeMutation,
  useDeleteEventTypeMutation,
} from "apis/eventType";
import { FaPlus } from "react-icons/fa6";
import EventTypeEdit from "components/eventType/Edit";
import EventTypeView from "components/eventType/View";
import Table from "components/ui/Table";

const CATEGORY_LABELS = {
  media: "Media",
  postProduction: "Post Production",
  print: "Print",
  inspection: "Inspection",
  meeting: "Meeting",
  milestone: "Milestone",
};

const CATEGORY_COLORS = {
  media: "bg-blue-100 text-blue-800",
  postProduction: "bg-purple-100 text-purple-800",
  print: "bg-yellow-100 text-yellow-800",
  inspection: "bg-green-100 text-green-800",
  meeting: "bg-orange-100 text-orange-800",
  milestone: "bg-red-100 text-red-800",
};

const formatDuration = (minutes) => {
  if (!minutes) return "-";
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs === 0) return `${mins} min`;
  if (mins === 0) return `${hrs} hr`;
  return `${hrs}h ${mins}m`;
};

const EventTypes = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useEventTypesQuery();

  const { mutate: createEventType, isLoading: isCreateLoading } =
    useAddEventTypeMutation();
  const { mutate: updateEventType } = useUpdateEventTypeMutation();
  const { mutate: deleteEventType, isLoading: isDeleteLoading } =
    useDeleteEventTypeMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "key",
        header: "Key",
        cell: (row) => <span className="text-sm font-mono">{row.key}</span>,
        isFilter: true,
      },
      {
        accessorKey: "name",
        header: "Event Name",
        isFilter: true,
        minWidth: "180px",
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: (row) => {
          const cat = row.category;
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                CATEGORY_COLORS[cat] || "bg-gray-100 text-gray-800"
              }`}
            >
              {CATEGORY_LABELS[cat] || cat}
            </span>
          );
        },
        isFilter: true,
      },
      {
        accessorKey: "defaultDurationMinutes",
        header: "Duration",
        cell: (row) => (
          <span className="text-sm font-medium">
            {formatDuration(row.defaultDurationMinutes)}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "requiresContractor",
        header: "Contractor",
        cell: (row) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              row.requiresContractor
                ? "bg-orange-100 text-orange-800"
                : "bg-green-100 text-green-800"
            }`}
          >
            {row.requiresContractor ? "Required" : "Not Required"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "requiresAgent",
        header: "Agent",
        cell: (row) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              row.requiresAgent
                ? "bg-blue-100 text-blue-800"
                : "bg-green-100 text-green-800"
            }`}
          >
            {row.requiresAgent ? "Required" : "Not Required"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "allowMultiple",
        header: "Multiple",
        cell: (row) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              row.allowMultiple
                ? "bg-purple-100 text-purple-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {row.allowMultiple ? "Yes" : "No"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (row) => (
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleDateString("en-AU", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Event Type"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Event Type"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "100px",
      },
    ],
    []
  );

  if (isDataPending) return <PageLoader />;

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading event types: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedEventType = data?.find((et) => et._id === selectedId);

  const handleEditSubmit = (formData) => {
    updateEventType({ id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = (formData) => {
    createEventType(formData);
    setIsAddOpen(false);
  };

  const handleDelete = () => {
    deleteEventType(selectedId);
    setIsDeleteOpen(false);
  };

  const handleRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Event Type"
        className="max-w-4xl"
      >
        <EventTypeEdit
          initialData={selectedEventType}
          onSubmit={handleEditSubmit}
        />
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Event Type"
        className="max-w-4xl"
      >
        <EventTypeEdit initialData={{}} onSubmit={handleCreateSubmit} />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Event Type"
      >
        <div>
          <p className="mb-4">
            Are you sure you want to delete "{selectedEventType?.name}"? This
            action cannot be undone.
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              No
            </button>
            <button
              onClick={handleDelete}
              className="black-button"
              disabled={isDeleteLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Event Type Details"
        className="max-w-3xl"
      >
        <EventTypeView eventType={selectedEventType} />
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Event Types"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddButton}
          onRowClick={handleRowClick}
        />
      </div>
    </>
  );
};

export default EventTypes;